import { useCallback, useEffect, useRef, useState } from "react";
import { NativeNotificationModal } from "@/components/commons/NativeNotificationModal";
import { isUserTap, subscribeNativeNotification, type NativeNotificationPayload } from "@/utils/native-notification";
import { markAsReadApi } from "@/api/services/main/app-notification";
import { sleep } from "@/utils/pieces";
import { isAuth } from "@/utils/api";

const MAX_PENDING_NOTIFICATIONS_POLLING = 5;
const PENDING_NOTIFICATIONS_POLLING_INTERVAL = 1000;

function markRead(payload: NativeNotificationPayload) {
  const id = Number(payload.data?.id);
  if (!isNaN(id) && id > 0) {
    markAsReadApi(id).catch(() => {});
  }
}

export const NativeNotificationListener = () => {
  const [notification, setNotification] = useState<NativeNotificationPayload | null>(null);
  const processedRef = useRef<Set<string>>(new Set());

  const showNotification = useCallback((payload: NativeNotificationPayload) => {
    if (!isAuth() || processedRef.current.has(payload.notificationId)) return;
    processedRef.current.add(payload.notificationId);
    markRead(payload);
    setNotification(payload);
  }, []);

  const handleConfirm = useCallback(() => {
    setNotification(null);
  }, []);

  useEffect(() => {
    let mounted = true;

    (async function waitAndFetchPending() {
      try {
        let counter = 0;
        while (mounted && typeof window.nativeDevice?.getPendingNotifications !== "function" && counter < MAX_PENDING_NOTIFICATIONS_POLLING) {
          await sleep(PENDING_NOTIFICATIONS_POLLING_INTERVAL);
          counter++;
        }

        if (!mounted) return;

        const pending = (await window.nativeDevice?.getPendingNotifications?.()) ?? [];
        pending.forEach((p) => {
          if (isUserTap(p.source)) showNotification(p);
        });
      } catch {
        /* native bridge not available or errored */
      }
    })();

    const unsubscribe = subscribeNativeNotification((payload) => {
      if (isUserTap(payload.source)) {
        showNotification(payload);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [showNotification]);

  return notification && <NativeNotificationModal open title={notification.title} body={notification.body} onConfirm={handleConfirm} />;
};
